import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback mirrors the backend seed (Season 2 calendar from @gridmasters_ae)
// so the section still renders if the API is unreachable.
const FALLBACK = [
  { n: 1, season: 2026, venue: "Yas Marina", layout: "GP Circuit", circuit: "Yas Marina - GP Circuit", region: "Abu Dhabi", date: "2026-10-24", image: "/photos/round-1-765lt-39-yas-client.webp", entries_opened: true },
  { n: 2, season: 2026, venue: "Dubai Autodrome", layout: "Club Circuit", circuit: "Dubai Autodrome - Club Circuit", region: "Dubai", date: "2026-11-08", image: "/photos/round-2-supra-esses.webp", entries_opened: false },
  { n: 3, season: 2026, venue: "Dubai Autodrome", layout: "National Circuit", circuit: "Dubai Autodrome - National Circuit", region: "Dubai", date: "2026-12-20", image: "/photos/round-3-mx5-banking.webp", entries_opened: false },
  { n: 4, season: 2026, venue: "Yas Marina", layout: "South Circuit", circuit: "Yas Marina - South Circuit", region: "Abu Dhabi", date: "2027-01-03", image: "/photos/round-4-yas-m4.webp", entries_opened: false },
  { n: 5, season: 2026, venue: "Dubai Autodrome", layout: "GP Circuit", circuit: "Dubai Autodrome - GP Circuit", region: "Dubai", date: "2027-02-21", image: "/photos/round-5-cayman-garage.webp", entries_opened: false },
  { n: 6, season: 2026, venue: "Yas Marina", layout: "GP Corkscrew Circuit", circuit: "Yas Marina - GP Corkscrew Circuit", region: "Abu Dhabi", date: "2027-03-27", image: "/photos/round-6-yas-gantry-car.webp", entries_opened: false },
];

// Calendar day in the UAE (Asia/Dubai) - the series runs on Gulf time.
export function todayUae() {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dubai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

// Now, as a Dubai wall-clock string "YYYY-MM-DDTHH:MM" (Asia/Dubai).
function nowUaeParts() {
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Dubai", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date()).map((x) => [x.type, x.value]));
    return `${p.year}-${p.month}-${p.day}T${p.hour === "24" ? "00" : p.hour}:${p.minute}`;
  } catch {
    return new Date().toISOString().slice(0, 16);
  }
}

// Default close: 2 days before the event day at 13:00 Dubai (client, 2026-10-19).
function defaultCloseLocal(dateIso) {
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 2);
  return `${d.toISOString().slice(0, 10)}T13:00`;
}

// Mirrors backend compute_round_status (the API is the source of truth; this
// only runs for the bundled fallback calendar while the network is unreachable).
// status: completed / live (event day) / next / upcoming. is_main: the featured
// round - the live one on event day, else the next one. entries: open / closed / soon.
export function computeStatus(rounds, todayIso) {
  const nowLocal = nowUaeParts();
  const today = todayIso || nowLocal.slice(0, 10);
  let nextFound = false;
  const ordered = [...rounds].sort((a, b) => a.n - b.n).map((r) => {
    let status = "upcoming";
    if (r.date < today) status = "completed";
    else if (r.date === today) status = "live";
    else if (!nextFound) { status = "next"; nextFound = true; }
    return { ...r, status };
  });
  const main = ordered.find((r) => r.status === "live") || ordered.find((r) => r.status === "next");
  return ordered.map((r) => {
    const is_main = !!main && r.n === main.n;
    const closeLocal = r.entries_close_override ? r.entries_close_override.slice(0, 16) : defaultCloseLocal(r.date);
    let entries = "soon";
    if (r.status === "completed") entries = "closed";
    // Client, 2026-10-20: entries are per round - any opened round takes
    // entries until its close time; is_main only decides placement.
    else if (r.entries_opened) entries = nowLocal >= closeLocal ? "closed" : "open";
    return { ...r, is_main, entries, entries_close_at: `${closeLocal}:00+04:00` };
  });
}

/** The featured round: hero card, landing card, timeline dot. */
export const mainRound = (rounds) => rounds.find((r) => r.is_main) || rounds.find((r) => r.status === "next") || rounds.find((r) => r.status === "live") || null;

/** The round sign-up leads to (client, 2026-10-22). In order:
 *   1. the featured round while it is taking entries;
 *   2. else the earliest other round that is open (Round 1 closed on race
 *      week, Round 2 on sale -> Round 2);
 *   3. else the earliest round still to come that has NOT opened yet - its
 *      page reads "Opening soon" (Round 1 closed, Round 2 not yet on sale ->
 *      Round 2; Rounds 1-2 done, Round 3 not open -> Round 3);
 *   4. else the featured round (everything left is closed) - its page reads
 *      "Entries closed, please contact management".
 *  The button itself ALWAYS says "Sign Up" and is always clickable; the round's
 *  own page carries the state, never the button. */
export const enterRound = (rounds) => {
  const main = mainRound(rounds);
  if (main && main.entries === "open") return main;
  const ahead = rounds.filter((r) => r.status !== "completed").sort((a, b) => a.n - b.n);
  return ahead.find((r) => r.entries === "open") || ahead.find((r) => r.entries === "soon") || main || ahead[0] || null;
};

export const enterPathFor = (n) => `/events/ultimate-lap/enter/${n}`;

// Single source of truth for the sign-up call to action (hero, nav, cards).
// kind: open | done | loading. `round.entries` says what the driver will find
// (open form / opening soon / closed notice) - the label never changes.
export function entryCta(rounds) {
  const target = enterRound(rounds);
  if (target) return { kind: "open", round: target, label: "Sign Up", href: enterPathFor(target.n), entries: target.entries };
  if (rounds.length) return { kind: "done", label: "Season complete", href: "/events/ultimate-lap/results" };
  return { kind: "loading", label: "Sign Up", href: "#rounds" };
}

// Stale-while-revalidate cache so every page that shows rounds paints on the
// first frame (module memory first, then this tab's sessionStorage, then the
// bundled calendar) and quietly refreshes from the API.
const memCache = new Map();
const SS_KEY = (season) => `gm_rounds_${season}`;

function readCache(season) {
  if (memCache.has(season)) return memCache.get(season);
  try {
    const raw = sessionStorage.getItem(SS_KEY(season));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) { memCache.set(season, parsed); return parsed; }
    }
  } catch { /* ignore */ }
  return null;
}
function writeCache(season, data) {
  memCache.set(season, data);
  try { sessionStorage.setItem(SS_KEY(season), JSON.stringify(data)); } catch { /* ignore */ }
}

export function prefetchRounds(season = 2026) {
  if (memCache.has(season)) return Promise.resolve(memCache.get(season));
  return axios.get(`${API}/rounds`, { params: { season } })
    .then(({ data }) => { const d = Array.isArray(data) && data.length ? data : computeStatus(FALLBACK); writeCache(season, d); return d; })
    .catch(() => computeStatus(FALLBACK));
}

export function useRounds(season = 2026) {
  const cached = readCache(season);
  // Never block on the network: cached calendar or the bundled one (same seed,
  // statuses computed locally) renders immediately.
  const [rounds, setRounds] = useState(() => cached || computeStatus(FALLBACK));
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/rounds`, { params: { season } });
        if (alive && Array.isArray(data) && data.length) { writeCache(season, data); setRounds(data); }
      } catch (err) {
        console.warn("rounds fetch failed", err?.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [season]);
  return { rounds, loading };
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Round day starts 09:00 local on the calendar date.
export function roundDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 9, 0, 0, 0);
}
export function fmtLong(iso) {
  const dt = roundDate(iso);
  return `${DAYS[dt.getDay()]} ${String(dt.getDate()).padStart(2, "0")} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}
const DAYS_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ordinal = (d) => { const v = d % 100; return d + (v >= 11 && v <= 13 ? "th" : ["th", "st", "nd", "rd"][Math.min(d % 10, 4)] || "th"); };
/* "Saturday 24th October 2026" - the feature round card (client, 2026-10-09). */
export function fmtFull(iso) {
  const dt = roundDate(iso);
  return `${DAYS_FULL[dt.getDay()]} ${ordinal(dt.getDate())} ${MONTHS_FULL[dt.getMonth()]} ${dt.getFullYear()}`;
}
export function fmtShort(iso) {
  const dt = roundDate(iso);
  return `${String(dt.getDate()).padStart(2, "0")} ${MONTHS[dt.getMonth()]}`;
}

export function statusLabel(r) {
  if (r.status === "completed") return "Round complete";
  if (r.status === "live") return "Race day";
  if (r.is_main) return r.entries === "open" ? "Next round. Entries open" : r.entries === "closed" ? "Next round. Entries closed" : "Next round";
  if (r.entries === "open") return "Entries open";
  if (r.entries === "closed") return "Entries closed";
  return "Opening soon";
}

